/**
 * JobAdder careers listing — shared React Query cache for /server/jobs.
 *
 * Prevents duplicate fetches when IntlProvider re-renders (locale/translations)
 * or when multiple CareersJobBoard instances mount on the same page.
 */

import {useQuery} from '@tanstack/react-query';

export const JOBS_QUERY_KEY = ['careers', 'jobs'];

const JOBS_STALE_TIME_MS = 5 * 60 * 1000;

async function fetchJobsFromApi() {
  const response = await fetch('/server/jobs');
  const data = await response.json();

  if (!data.ok) {
    const error = new Error(data.error || 'FETCH_FAILED');
    error.code = 'FETCH_FAILED';
    throw error;
  }

  if (data.warning) {
    console.warn('[Careers] API Warning:', data.warning);
  }

  return {
    jobs: data.jobs || [],
    total: data.total ?? (data.jobs || []).length,
    warning: data.warning,
  };
}

/**
 * @param {Object} [options]
 * @param {import('@tanstack/react-query').UseQueryOptions} [options.queryOptions]
 * @returns {import('@tanstack/react-query').UseQueryResult<{jobs: Array, total: number, warning?: string}>}
 */
export function useJobs(options = {}) {
  const {queryOptions = {}} = options;

  return useQuery({
    queryKey: JOBS_QUERY_KEY,
    queryFn: fetchJobsFromApi,
    staleTime: JOBS_STALE_TIME_MS,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    ...queryOptions,
  });
}
